import { useEffect, useState, useCallback } from "react";
import { MessageSquareHeart, Pencil, Trash2, Loader2, Plus, Youtube, Film, EyeOff } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { API, formatApiError } from "@/lib/api";
import TestimonialForm from "@/components/TestimonialForm";

export default function TestimonialsPanel() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [cfg, setCfg] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, c] = await Promise.all([api.get("/admin/testemunhos"), api.get("/admin/config")]);
      setItems(t.data);
      setCfg(c.data);
    } catch (e) {
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (t) => {
    if (!window.confirm(`Excluir o testemunho "${t.titulo}"?`)) return;
    try {
      await api.delete(`/admin/testemunhos/${t.id}`);
      toast.success("Testemunho excluído.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!items) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  return (
    <div data-testid="testimonials-panel" className="space-y-5">
      {cfg && !cfg.testemunhos_ativo && (
        <div data-testid="testimonials-disabled-notice" className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm inline-flex items-center gap-2">
          <EyeOff className="w-4 h-4" /> O carrossel está desligado. Ligue a chave "Testemunhos em vídeo" na aba Configurações para exibir na página pública.
        </div>
      )}
      {creating ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <h3 className="font-heading text-xl font-bold text-slate-900 mb-4">Novo testemunho</h3>
          <TestimonialForm onSaved={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} />
        </div>
      ) : (
        <button data-testid="testimonial-new-button" onClick={() => setCreating(true)} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-800 hover:bg-blue-700 text-white font-bold shadow-md transition-colors">
          <Plus className="w-5 h-5" /> Novo testemunho
        </button>
      )}

      {items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500">
          <MessageSquareHeart className="w-10 h-10 mx-auto text-blue-300" />
          <p className="mt-3">Nenhum testemunho cadastrado ainda.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((t) => (
            <div key={t.id} data-testid={`testimonial-row-${t.id}`} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
              {editing === t.id ? (
                <TestimonialForm item={t} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
              ) : (
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  {t.capa ? (
                    <img src={`${API}/files/${t.capa}?v=${encodeURIComponent(t.updated_at)}`} alt="" className="w-11 h-14 rounded-lg object-cover border border-slate-200 shrink-0" />
                  ) : (
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${t.video_url ? "bg-rose-50 text-rose-600" : "bg-blue-50 text-blue-800"}`}>
                      {t.video_url ? <Youtube className="w-5 h-5" /> : <Film className="w-5 h-5" />}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-heading text-lg font-bold text-slate-900">{t.titulo}</p>
                      {!t.ativo && <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-600 border border-slate-200">OCULTO</span>}
                    </div>
                    <p className="text-sm text-slate-500">{t.nome || "—"} · Ordem {t.ordem} · {t.video_url ? "YouTube" : "Vídeo enviado"}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button data-testid={`testimonial-edit-${t.id}`} onClick={() => setEditing(t.id)} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm border border-slate-200"><Pencil className="w-4 h-4" /> Editar</button>
                    <button data-testid={`testimonial-delete-${t.id}`} onClick={() => remove(t)} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 font-semibold text-sm border border-rose-200"><Trash2 className="w-4 h-4" /> Excluir</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
